import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Calendar, Plus, Sparkles, Loader2, Trash2, X, Check, ChevronLeft, ChevronRight,
  FileText, Instagram, Facebook, Linkedin, MapPin, Star,
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getToken() {
  return document.cookie.split('; ').find(r => r.startsWith('session_token='))?.split('=')[1]
    || localStorage.getItem('session_token') || '';
}
const authHeaders = () => ({ Authorization: `Bearer ${getToken()}` });

const PLATFORM_ICONS = {
  profile: FileText, instagram: Instagram, facebook: Facebook,
  linkedin: Linkedin, google: Star, nextdoor: MapPin,
};

const PLATFORM_COLORS = {
  profile: '#1D6B4F', instagram: '#E1306C', facebook: '#1877F2',
  linkedin: '#0A66C2', google: '#EA4335', nextdoor: '#8ED500',
};

const TYPE_LABELS = {
  article: 'Article', social_post: 'Social Post', review_response: 'Review',
};

export default function CalendarTab() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [suggesting, setSuggesting] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });

  const fetchCalendar = async () => {
    try {
      const res = await axios.get(`${API}/content/calendar`, { headers: authHeaders() });
      setEvents(res.data.events || []);
    } catch (e) { /* fetch failed */ }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchCalendar(); }, []);

  const handleSuggest = async () => {
    setSuggesting(true);
    setSuggestions([]);
    try {
      const res = await axios.post(`${API}/content/calendar/suggest`, {}, { headers: authHeaders() });
      setSuggestions(res.data.suggestions || []);
    } catch (e) { /* suggest failed */ }
    finally { setSuggesting(false); }
  };

  const handleAddSuggestion = async (suggestion) => {
    try {
      await axios.post(`${API}/content/schedule`, {
        title: suggestion.title,
        date: suggestion.date,
        platform: suggestion.platform,
        content_type: suggestion.content_type,
        notes: suggestion.notes || '',
      }, { headers: authHeaders() });
      setSuggestions(prev => prev.filter(s => s !== suggestion));
      fetchCalendar();
    } catch (e) { /* add failed */ }
  };

  const handleDelete = async (scheduleId) => {
    try {
      await axios.delete(`${API}/content/schedule/${scheduleId}`, { headers: authHeaders() });
      fetchCalendar();
    } catch (e) { /* delete failed */ }
  };

  const handleAddEvent = async (eventData) => {
    try {
      await axios.post(`${API}/content/schedule`, eventData, { headers: authHeaders() });
      setShowAddModal(false);
      fetchCalendar();
    } catch (e) { /* add failed */ }
  };

  // Calendar helpers
  const prevMonth = () => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  const nextMonth = () => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));

  const daysInMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0).getDate();
  const firstDayOfWeek = currentMonth.getDay();
  const monthLabel = currentMonth.toLocaleString('en-US', { month: 'long', year: 'numeric' });

  // Group events by date for the calendar grid
  const eventsByDate = {};
  events.forEach(e => {
    const d = e.date?.slice(0, 10);
    if (d) {
      if (!eventsByDate[d]) eventsByDate[d] = [];
      eventsByDate[d].push(e);
    }
  });

  const today = new Date().toISOString().slice(0, 10);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="cal-loading">
        <Loader2 size={24} className="animate-spin" style={{ color: '#8C7E6E' }} />
      </div>
    );
  }

  return (
    <div data-testid="calendar-tab">
      {/* Actions bar */}
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 px-4 py-2.5 text-[12px] font-medium transition-all"
          style={{ backgroundColor: '#2C2520', color: '#F0EBE3' }}
          data-testid="add-event-btn"
        >
          <Plus size={14} /> Schedule Content
        </button>
        <button
          onClick={handleSuggest}
          disabled={suggesting}
          className="flex items-center gap-2 px-4 py-2.5 text-[12px] font-medium transition-all"
          style={{ backgroundColor: '#F7F4EF', border: '1px solid #DDD5C8', color: '#3C3226', opacity: suggesting ? 0.6 : 1 }}
          data-testid="suggest-calendar-btn"
        >
          {suggesting ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
          {suggesting ? 'Generating...' : 'AI Suggestions'}
        </button>
      </div>

      {/* AI Suggestions */}
      <AnimatePresence>
        {suggestions.length > 0 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mb-6 overflow-hidden"
          >
            <p className="text-[10px] uppercase tracking-widest mb-3" style={{ color: '#B8985A' }}>
              AI Suggestions — Click to add
            </p>
            <div className="space-y-2">
              {suggestions.map((s, i) => {
                const Icon = PLATFORM_ICONS[s.platform] || FileText;
                const color = PLATFORM_COLORS[s.platform] || '#3C3226';
                return (
                  <motion.div
                    key={`${s.date}-${i}`}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.05 }}
                    className="flex items-center gap-3 p-3"
                    style={{ backgroundColor: '#F7F4EF', border: '1px dashed #B8985A' }}
                    data-testid={`suggestion-${i}`}
                  >
                    <div className="w-8 h-8 flex items-center justify-center" style={{ backgroundColor: `${color}10` }}>
                      <Icon size={14} style={{ color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] font-medium truncate" style={{ color: '#3C3226' }}>{s.title}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-[10px]" style={{ color: '#8C7E6E' }}>{s.date}</span>
                        <span className="text-[10px]" style={{ color }}>{s.platform}</span>
                        {s.notes && <span className="text-[10px] truncate" style={{ color: '#8C7E6E' }}>{s.notes}</span>}
                      </div>
                    </div>
                    <button
                      onClick={() => handleAddSuggestion(s)}
                      className="flex items-center gap-1 px-3 py-1.5 text-[11px] font-medium"
                      style={{ backgroundColor: '#1D6B4F', color: '#F0EBE3' }}
                      data-testid={`add-suggestion-${i}`}
                    >
                      <Plus size={12} /> Add
                    </button>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Calendar Grid */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <button onClick={prevMonth} className="p-2" style={{ color: '#5C5347' }} data-testid="cal-prev">
            <ChevronLeft size={18} />
          </button>
          <h3 className="text-[15px] font-medium" style={{ color: '#3C3226' }} data-testid="cal-month-label">{monthLabel}</h3>
          <button onClick={nextMonth} className="p-2" style={{ color: '#5C5347' }} data-testid="cal-next">
            <ChevronRight size={18} />
          </button>
        </div>

        {/* Day headers */}
        <div className="grid grid-cols-7 mb-1">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
            <div key={d} className="text-center text-[10px] uppercase tracking-widest py-2" style={{ color: '#8C7E6E' }}>
              {d}
            </div>
          ))}
        </div>

        {/* Day cells */}
        <div className="grid grid-cols-7 gap-px" style={{ backgroundColor: '#DDD5C8' }}>
          {/* Empty cells for offset */}
          {Array.from({ length: firstDayOfWeek }).map((_, i) => (
            <div key={`empty-${i}`} className="min-h-[72px] p-1" style={{ backgroundColor: '#FDFBF7' }} />
          ))}
          {/* Day cells */}
          {Array.from({ length: daysInMonth }).map((_, i) => {
            const day = i + 1;
            const dateStr = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const dayEvents = eventsByDate[dateStr] || [];
            const isToday = dateStr === today;

            return (
              <div
                key={day}
                className="min-h-[72px] p-1.5"
                style={{ backgroundColor: isToday ? '#1D6B4F08' : '#FDFBF7' }}
                data-testid={`cal-day-${dateStr}`}
              >
                <span
                  className="text-[11px] font-medium block mb-1"
                  style={{
                    color: isToday ? '#1D6B4F' : '#5C5347',
                    fontWeight: isToday ? 700 : 400,
                  }}
                >
                  {day}
                </span>
                {dayEvents.slice(0, 2).map((ev, j) => {
                  const color = ev.type === 'scheduled' ? '#B8985A' : ev.type === 'published' ? '#1D6B4F' : '#8C7E6E';
                  return (
                    <div
                      key={`${ev.id}-${j}`}
                      className="text-[9px] leading-tight truncate px-1 py-0.5 mb-0.5"
                      style={{ backgroundColor: `${color}15`, color }}
                      title={ev.title}
                    >
                      {ev.title}
                    </div>
                  );
                })}
                {dayEvents.length > 2 && (
                  <span className="text-[9px]" style={{ color: '#8C7E6E' }}>+{dayEvents.length - 2} more</span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Upcoming events list */}
      {events.filter(e => e.type === 'scheduled').length > 0 && (
        <div className="mb-6">
          <p className="text-[10px] uppercase tracking-widest mb-3" style={{ color: '#B8985A' }}>Scheduled</p>
          <div className="space-y-2">
            {events.filter(e => e.type === 'scheduled').map((ev, i) => {
              const Icon = PLATFORM_ICONS[ev.platform] || FileText;
              const color = PLATFORM_COLORS[ev.platform] || '#3C3226';
              return (
                <div
                  key={ev.id}
                  className="flex items-center gap-3 p-3"
                  style={{ backgroundColor: '#F7F4EF', border: '1px solid #DDD5C8' }}
                  data-testid={`scheduled-${i}`}
                >
                  <div className="w-8 h-8 flex items-center justify-center" style={{ backgroundColor: `${color}10` }}>
                    <Icon size={14} style={{ color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-medium truncate" style={{ color: '#3C3226' }}>{ev.title}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-[10px]" style={{ color: '#8C7E6E' }}>{ev.date}</span>
                      <span className="text-[10px]" style={{ color }}>{ev.platform}</span>
                    </div>
                  </div>
                  <button
                    onClick={() => handleDelete(ev.id)}
                    className="p-1.5"
                    style={{ color: '#C67A5C' }}
                    data-testid={`delete-scheduled-${i}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {events.length === 0 && !suggestions.length && <EmptyCalendar onSuggest={handleSuggest} suggesting={suggesting} />}

      {/* Add Event Modal */}
      <AnimatePresence>
        {showAddModal && <AddEventModal onClose={() => setShowAddModal(false)} onAdd={handleAddEvent} />}
      </AnimatePresence>
    </div>
  );
}

function EmptyCalendar({ onSuggest, suggesting }) {
  return (
    <div className="text-center py-16" data-testid="cal-empty">
      <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: '#B8985A10' }}>
        <Calendar size={28} strokeWidth={1} style={{ color: '#B8985A' }} />
      </div>
      <h3 className="text-base font-medium mb-2" style={{ color: '#3C3226' }}>Your content calendar is empty</h3>
      <p className="text-[13px] max-w-xs mx-auto mb-6" style={{ color: '#8C7E6E' }}>
        Schedule content ahead of time or let AI suggest a 2-week plan tailored to your business.
      </p>
      <button
        onClick={onSuggest}
        disabled={suggesting}
        className="inline-flex items-center gap-2 px-6 py-3 text-[13px] font-medium"
        style={{ backgroundColor: '#2C2520', color: '#F0EBE3', opacity: suggesting ? 0.6 : 1 }}
        data-testid="suggest-empty-btn"
      >
        {suggesting ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
        Generate 2-Week Plan
      </button>
    </div>
  );
}

function AddEventModal({ onClose, onAdd }) {
  const [title, setTitle] = useState('');
  const [date, setDate] = useState('');
  const [platform, setPlatform] = useState('profile');
  const [contentType, setContentType] = useState('article');
  const [notes, setNotes] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!title.trim() || !date) return;
    onAdd({ title: title.trim(), date, platform, content_type: contentType, notes });
  };

  const platforms = [
    { id: 'profile', label: 'Blog' },
    { id: 'facebook', label: 'Facebook' },
    { id: 'instagram', label: 'Instagram' },
    { id: 'linkedin', label: 'LinkedIn' },
    { id: 'google', label: 'Google' },
  ];

  const types = [
    { id: 'article', label: 'Article' },
    { id: 'social_post', label: 'Social Post' },
    { id: 'review_response', label: 'Review Response' },
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.4)' }}
      onClick={onClose}
    >
      <motion.form
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        onClick={e => e.stopPropagation()}
        onSubmit={handleSubmit}
        className="w-full max-w-md p-6"
        style={{ backgroundColor: '#FDFBF7', border: '1px solid #DDD5C8' }}
        data-testid="add-event-modal"
      >
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-[15px] font-medium" style={{ color: '#3C3226' }}>Schedule Content</h3>
          <button type="button" onClick={onClose} className="p-1"><X size={18} style={{ color: '#8C7E6E' }} /></button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-[10px] uppercase tracking-widest mb-1.5" style={{ color: '#8C7E6E' }}>Title</label>
            <input
              value={title}
              onChange={e => setTitle(e.target.value)}
              className="w-full p-2.5 text-[13px] outline-none"
              style={{ backgroundColor: '#F7F4EF', border: '1px solid #DDD5C8', color: '#3C3226' }}
              placeholder="Content title"
              data-testid="event-title-input"
            />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest mb-1.5" style={{ color: '#8C7E6E' }}>Date</label>
            <input
              type="date"
              value={date}
              onChange={e => setDate(e.target.value)}
              className="w-full p-2.5 text-[13px] outline-none"
              style={{ backgroundColor: '#F7F4EF', border: '1px solid #DDD5C8', color: '#3C3226' }}
              data-testid="event-date-input"
            />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest mb-1.5" style={{ color: '#8C7E6E' }}>Platform</label>
            <div className="flex flex-wrap gap-2">
              {platforms.map(p => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setPlatform(p.id)}
                  className="px-3 py-1.5 text-[11px] font-medium"
                  style={{
                    backgroundColor: platform === p.id ? '#2C2520' : '#F7F4EF',
                    color: platform === p.id ? '#F0EBE3' : '#5C5347',
                    border: `1px solid ${platform === p.id ? '#2C2520' : '#DDD5C8'}`,
                  }}
                  data-testid={`event-platform-${p.id}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest mb-1.5" style={{ color: '#8C7E6E' }}>Type</label>
            <div className="flex flex-wrap gap-2">
              {types.map(t => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setContentType(t.id)}
                  className="px-3 py-1.5 text-[11px] font-medium"
                  style={{
                    backgroundColor: contentType === t.id ? '#2C2520' : '#F7F4EF',
                    color: contentType === t.id ? '#F0EBE3' : '#5C5347',
                    border: `1px solid ${contentType === t.id ? '#2C2520' : '#DDD5C8'}`,
                  }}
                  data-testid={`event-type-${t.id}`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest mb-1.5" style={{ color: '#8C7E6E' }}>Notes</label>
            <input
              value={notes}
              onChange={e => setNotes(e.target.value)}
              className="w-full p-2.5 text-[13px] outline-none"
              style={{ backgroundColor: '#F7F4EF', border: '1px solid #DDD5C8', color: '#3C3226' }}
              placeholder="Optional notes"
              data-testid="event-notes-input"
            />
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <button
            type="submit"
            className="flex-1 flex items-center justify-center gap-2 py-3 text-[13px] font-medium"
            style={{ backgroundColor: '#2C2520', color: '#F0EBE3' }}
            data-testid="submit-event-btn"
          >
            <Check size={14} /> Schedule
          </button>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-3 text-[13px]"
            style={{ border: '1px solid #DDD5C8', color: '#5C5347' }}
            data-testid="cancel-event-btn"
          >
            Cancel
          </button>
        </div>
      </motion.form>
    </motion.div>
  );
}
